import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, List, Settings, Layout, Key, ShieldCheck, HelpCircle, Users, Zap, Clock, Smartphone, Tv, Wifi, Globe, HardDrive } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Configurar IPTV Pela Primeira Vez", href: "/blog/como-configurar-iptv-primeira-vez" }
  ];

  const faq = [
    {
      question: "Quanto tempo leva a primeira configuração?",
      answer: "Para um usuário comum, utilizando nossos aplicativos otimizados como o One X, a configuração leva menos de 5 minutos. Em sistemas manuais, pode levar até 15 minutos."
    },
    {
      question: "Preciso de um técnico para configurar?",
      answer: "Não. Seguindo este guia denso e detalhado, qualquer pessoa consegue realizar a ativação. Nossa Central de Ajuda foi desenhada para dar autonomia total ao usuário."
    },
    {
      question: "Posso testar antes de assinar?",
      answer: "Sim, a P2 PRO oferece testes gratuitos para que você valide a compatibilidade do seu dispositivo e a estabilidade da sua rede antes de qualquer compromisso."
    },
    {
      question: "O que é M3U e Xtream Codes?",
      answer: "M3U é um formato de link único que contém toda a lista. Xtream Codes é uma API que divide os dados em Usuário, Senha e URL, sendo o método mais moderno e estável."
    }
  ];

  const benefits = [
    {
      title: "Autonomia Técnica",
      description: "Aprenda a resolver problemas sem depender de suporte externo.",
      icon: <Settings className="w-6 h-6" />
    },
    {
      title: "Estabilidade Máxima",
      description: "Configurações otimizadas que evitam 99% dos travamentos comuns.",
      icon: <Zap className="w-6 h-6" />
    },
    {
      title: "Conhecimento de Elite",
      description: "Entenda o que acontece 'por baixo do capô' do seu sistema de streaming.",
      icon: <Globe className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Auditoria de Rede",
      description: "Verifique sua velocidade e estabilidade antes de começar."
    },
    {
      title: "Coleta de Credenciais",
      description: "Tenha em mãos seu Usuário, Senha e URL fornecidos."
    },
    {
      title: "Seleção do Player",
      description: "Escolha o aplicativo que melhor se adapta ao seu hardware."
    },
    {
      title: "Ativação e Teste",
      description: "Realize o login e execute os testes de estresse iniciais."
    }
  ];

  const diferenciais = [
    {
      title: "Infraestrutura Dedicada",
      description: "Nossos servidores são dimensionados para suportar picos de ativação sem lentidão.",
      icon: <HardDrive className="w-6 h-6" />
    },
    {
      title: "Apps de Luxo",
      description: "Interfaces desenhadas para facilitar a vida do usuário final.",
      icon: <Layout className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. Introdução: O Novo Padrão de Entretenimento",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Configurar o IPTV pela primeira vez marca o início de uma nova era no consumo de mídia digital. Em 2026, o mercado de streaming evoluiu de simples listas de canais para ecossistemas complexos de entretenimento. No entanto, com a complexidade, surgem os desafios técnicos. Este guia foi criado para ser o recurso mais denso e definitivo sobre o assunto, garantindo que sua jornada na <strong>P2 PRO</strong> seja impecável.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Entender o funcionamento básico é o primeiro passo para o sucesso. O IPTV (Internet Protocol Television) não é apenas "televisão pela internet", é o gerenciamento inteligente de pacotes de dados de vídeo. Quando você configura seu sistema, está estabelecendo uma ponte de alta velocidade entre seu dispositivo e nossos servidores globais.
          </p>
        </div>
      )
    },
    {
      title: "2. Preparação do Ambiente: A Regra dos 3 Pilares",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg text-muted-foreground mb-6">Antes de inserir qualquer dado, você precisa garantir que sua infraestrutura doméstica suporte o tráfego de dados massivo que o streaming 4K exige.</p>
          <div className="grid md:grid-cols-3 gap-6 my-8">
            <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
              <h4 className="font-bold text-white mb-3 flex items-center gap-2"><Wifi className="w-5 h-5 text-electric" /> Estabilidade de Rede</h4>
              <p className="text-sm text-muted-foreground">Velocidade não é tudo. O <em>Jitter</em> e o <em>Ping</em> são vitais. Utilize cabo de rede sempre que possível.</p>
            </div>
            <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
              <h4 className="font-bold text-white mb-3 flex items-center gap-2"><HardDrive className="w-5 h-5 text-electric" /> Hardware Robusto</h4>
              <p className="text-sm text-muted-foreground">Smart TVs antigas têm pouca RAM. Considere um TV Box moderno para maior fluidez.</p>
            </div>
            <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
              <h4 className="font-bold text-white mb-3 flex items-center gap-2"><Key className="w-5 h-5 text-electric" /> Credenciais Válidas</h4>
              <p className="text-sm text-muted-foreground">Verifique se sua conta está ativa e se os dados de acesso são do padrão Xtream Codes.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Passo a Passo Detalhado da Ativação",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <div>
            <h4 className="text-white text-xl font-bold mb-4">Fase 1: Escolha e Instalação do Aplicativo</h4>
            <p className="text-muted-foreground">A escolha do player é determinante. Recomendamos o <strong>One X</strong> para Android e o <strong>IBO Player</strong> para Smart TVs Samsung/LG. Acesse a loja de aplicativos, realize o download e abra a interface inicial.</p>
          </div>
          <div className="bg-electric/5 p-8 rounded-3xl border border-electric/20">
            <h4 className="text-white text-xl font-bold mb-4">Fase 2: O Método Xtream Codes (API)</h4>
            <p className="text-muted-foreground mb-4">Diferente do link M3U (que é um arquivo estático), a API Xtream Codes permite uma comunicação dinâmica. No seu aplicativo, procure por "Login with Xtream Codes API". Você verá quatro campos:</p>
            <ul className="space-y-3 text-muted-foreground">
              <li><strong className="text-electric">Any Name:</strong> Apenas para sua identificação (ex: P2PRO Sala).</li>
              <li><strong className="text-electric">Username:</strong> Seu identificador enviado por e-mail.</li>
              <li><strong className="text-electric">Password:</strong> Sua senha alfanumérica segura.</li>
              <li><strong className="text-electric">URL/Host:</strong> O endereço do servidor (ex: http://servidor.xyz:8080).</li>
            </ul>
          </div>
          <div>
            <h4 className="text-white text-xl font-bold mb-4">Fase 3: Sincronização Inicial</h4>
            <p className="text-muted-foreground">Após clicar em login, o aplicativo fará o download da estrutura de categorias. Este processo pode levar de 30 segundos a 2 minutos. Não interrompa a conexão durante esta fase.</p>
          </div>
        </div>
      )
    },
    {
      title: "4. Otimização Pós-Configuração",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg text-muted-foreground mb-6">Para garantir uma experiência de luxo, realize as seguintes manutenções iniciais:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Configuração de DNS</h5>
                <p className="text-sm text-muted-foreground">Muitos provedores bloqueiam o sinal. Configure o DNS do Google (8.8.8.8) no seu roteador para burlar limitações.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Player de Vídeo Nativo</h5>
                <p className="text-sm text-muted-foreground">Nas configurações do app, selecione o player 'ExoPlayer' ou 'VLC' para melhor compatibilidade com filmes 4K.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    },
    {
      title: "Conclusão: Pronto para o Espetáculo",
      type: "featured" as const,
      content: (
        <div className="text-center space-y-6">
          <p className="text-lg text-white font-medium italic">"A tecnologia só é mágica quando funciona perfeitamente."</p>
          <p className="text-muted-foreground">Ao seguir este guia exaustivo, você não apenas configurou um serviço, mas estabeleceu um centro de mídia profissional em sua casa. Se ainda encontrar dificuldades, nossa equipe de suporte está à disposição.</p>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Central de Ajuda IPTV"
        title="Como Configurar IPTV Pela Primeira Vez: Guia Definitivo e Denso"
        subtitle="O recurso mais completo da internet para iniciantes: aprenda a configurar sua estrutura de streaming do zero com perfeição técnica."
        heroImage="https://images.unsplash.com/photo-1593305841991-05c297ba4575?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "A jornada para um entretenimento de elite começa com uma configuração técnica sólida. Não se trata apenas de 'clicar e assistir', mas de entender os parâmetros que garantem uma transmissão estável, segura e em altíssima definição.",
          "Neste guia denso da Central de Ajuda P2 PRO, mergulhamos profundamente nos detalhes que outros ignoram. Desde a auditoria da sua rede doméstica até a escolha cirúrgica do aplicativo, cobrimos cada milímetro do processo de ativação inicial.",
          "Prepare-se para transformar seu dispositivo em uma potência de streaming, eliminando erros comuns antes mesmo que eles aconteçam."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="🚀 Já Configurou mas quer Escalar?"
        ctaDescription="Agora que você dominou a técnica, que tal transformar esse conhecimento em lucro? Torne-se um revendedor Master P2 PRO."
        ctaButtonText="Quero Revender"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/como-configurar-iptv-primeira-vez")({
  head: () => ({
    meta: [
      { title: "Como Configurar IPTV Pela Primeira Vez - Guia Técnico Denso" },
      { name: "description", content: "O guia mais completo sobre configuração inicial de IPTV. Aprenda segredos técnicos, DNS, Xtream Codes e muito mais para uma experiência sem travamentos." },
    ],
  }),
  component: ArticlePage,
});
